/**
 * Adapters over `@pit/sim`'s pure target-selection helpers (#63).
 *
 * The sim helpers take a full `SimState`; the client only has a `WireSimState`
 * (server-authoritative slice — no `championId` / `castState.kind` etc.). This
 * module maps the wire shape into what the helpers read:
 *
 *   • `state.entities[seat]`      → seat lookup (seat === array index)
 *   • entity `.team`, `.id`, `.hp` — for enemy/ally + liveness selection
 *   • entity `.targets.{hostile,friendly}` — cycle anchors
 *
 * Fields the helpers never read (facing, castState) get zero/placeholder
 * values that satisfy the SimState type. Because every helper is a PURE READ
 * (no writes), that reconstruction is safe.
 *
 * Doing it this way — rather than reimplementing selection — keeps WP4/#63's
 * target semantics as the ONE source of truth. If the sim changes cycle order,
 * roster ordering, or liveness policy, the client picks it up for free.
 *
 * Every adapter shares `wireToSimState`; each is a one-line delegate that
 * mirrors the original `nextEnemyTargetWire` shape — including #815's
 * `nearestEnemyTargetWire` / `hasLiveEnemyTargetWire`, which is why "nearest"
 * is defined ONCE (in the sim) rather than re-derived here.
 */

import type { EntityId, Tick, WireSimState } from "@pit/protocol";
import {
  nextEnemyTarget,
  nearestEnemyTarget,
  hasLiveEnemyTarget,
  nextAllyTarget,
  enemyByIndex,
  allyByIndex,
  asEntityId,
  asHp,
  asSeatIndex,
  asTeamIndex,
  asTick,
  asUnits,
  FACING_DIR_N,
  NO_MOVE_INTENT,
} from "@pit/sim";
import type { AuraInstance, Entity as SimEntity, SimState } from "@pit/sim";

/**
 * Next enemy target for `seat` (Tab cycle → hostile slot), or `null` if none.
 * Delegates to `@pit/sim`'s `nextEnemyTarget`.
 */
export function nextEnemyTargetWire(
  snapshot: WireSimState,
  seat: number,
): EntityId | null {
  return nextEnemyTarget(wireToSimState(snapshot), seat);
}

/**
 * #815: NEAREST living, targetable enemy for `seat` (Tab + auto-acquire →
 * hostile slot), or `null` if none exists. Delegates to `@pit/sim`'s
 * `nearestEnemyTarget` — distance semantics, the corpse/stealth skips and the
 * lowest-seat tie-break all live there, exactly like the cycle above.
 */
export function nearestEnemyTargetWire(
  snapshot: WireSimState,
  seat: number,
): EntityId | null {
  return nearestEnemyTarget(wireToSimState(snapshot), seat);
}

/**
 * #815: does `seat`'s hostile slot still hold a living, targetable enemy?
 * Delegates to `@pit/sim`'s `hasLiveEnemyTarget` — the "do I need to acquire a
 * target?" predicate behind Tab and cast auto-acquire, so client and sim can
 * never disagree on what makes a target still valid.
 */
export function hasLiveEnemyTargetWire(snapshot: WireSimState, seat: number): boolean {
  return hasLiveEnemyTarget(wireToSimState(snapshot), seat);
}

/**
 * Next ally target for `seat` (friendly cycle → friendly slot), or `null` if
 * none. Delegates to `@pit/sim`'s `nextAllyTarget` (self included, self-first).
 */
export function nextAllyTargetWire(
  snapshot: WireSimState,
  seat: number,
): EntityId | null {
  return nextAllyTarget(wireToSimState(snapshot), seat);
}

/**
 * The `index`-th enemy for `seat` (arena direct-select → hostile slot), or
 * `null` when the roster slot is empty or holds a corpse. Delegates to
 * `@pit/sim`'s `enemyByIndex`.
 */
export function enemyByIndexWire(
  snapshot: WireSimState,
  seat: number,
  index: number,
): EntityId | null {
  return enemyByIndex(wireToSimState(snapshot), seat, index);
}

/**
 * The `index`-th ally for `seat` (party direct-select → friendly slot; index 0
 * = self), or `null` when the roster slot is empty or holds a corpse.
 * Delegates to `@pit/sim`'s `allyByIndex`.
 */
export function allyByIndexWire(
  snapshot: WireSimState,
  seat: number,
  index: number,
): EntityId | null {
  return allyByIndex(wireToSimState(snapshot), seat, index);
}

/**
 * The direct-select INDEX at which `entityId` is the `seat`'s arena (enemy)
 * hotkey target, or `null` when `entityId` is not a live-targetable enemy of
 * `seat`. The inverse of `enemyByIndexWire`: it asks the SAME roster resolver
 * "for which index does this id come back?" so the answer can never diverge
 * from the key that actually selects the unit (#136 nameplates). Dead / stealth
 * / non-enemy ids return `null` (never directly-selectable → no nameplate).
 */
export function enemyTargetIndexWire(
  snapshot: WireSimState,
  seat: number,
  entityId: EntityId,
): number | null {
  const state = wireToSimState(snapshot);
  // The enemy roster can hold at most one entry per entity; scanning every
  // possible seat slot is an exhaustive, order-preserving inversion.
  for (let i = 0; i < state.entities.length; i++) {
    if (enemyByIndex(state, seat, i) === entityId) return i;
  }
  return null;
}

/**
 * The direct-select INDEX at which `entityId` is the `seat`'s party (ally)
 * hotkey target (index 0 = self), or `null` when `entityId` is not a
 * live-targetable ally of `seat`. The ally mirror of `enemyTargetIndexWire`,
 * inverting `allyByIndexWire` against the shared #63/#116 roster ordering.
 */
export function allyTargetIndexWire(
  snapshot: WireSimState,
  seat: number,
  entityId: EntityId,
): number | null {
  const state = wireToSimState(snapshot);
  for (let i = 0; i < state.entities.length; i++) {
    if (allyByIndex(state, seat, i) === entityId) return i;
  }
  return null;
}

/**
 * Build a minimal SimState-shaped view over the wire snapshot for the pure sim
 * helpers. Only the fields the helpers read matter; the rest are structural
 * placeholders (typed via `as*` brand escape hatches exported by @pit/sim
 * exactly for this pattern).
 */
function wireToSimState(snapshot: WireSimState): SimState {
  const entities: SimEntity[] = snapshot.entities.map((e) => {
    const cd: readonly Tick[] = [];
    // The wire carries a render sector, but SimEntity.facing is a precise
    // direction (#62). These helpers never read facing, so use the canonical
    // North placeholder rather than reconstruct a direction from the sector.
    const simEntity: SimEntity = {
      id: asEntityId(e.entityId),
      seat: asSeatIndex(e.seat),
      team: asTeamIndex(e.team),
      // #574: confirmed NOT a default-champion dependency — none of
      // `nextEnemyTarget`/`nextAllyTarget`/`enemyByIndex`/`allyByIndex` (nor
      // their `isTargetableBy`/`isAlive`/roster helpers) take a `bundle` or
      // otherwise read `championId`; this placeholder value is structurally
      // unreachable, so it stays a literal (not resolved via canonical
      // lookup — there is nothing here that could ever desync).
      // eslint-disable-next-line pit/no-hardcoded-champion-id -- #576: see above.
      championId: "chm_BJ4RLXqiBG", // placeholder (slug: warrior) — helper does not read it
      abilityIds: null,
      x: asUnits(e.x),
      y: asUnits(e.y),
      facing: FACING_DIR_N,
      hp: asHp(e.hp),
      moveIntent: NO_MOVE_INTENT,
      // #351: targeting reconstruction never reads the strafe-axis convention;
      // the wire snapshot omits it, so the default sentinel is the correct
      // read-only placeholder.
      strafeMode: "facing" as const,
      castState: { kind: "idle" as const },
      // #93: targeting reconstruction never reads the auto-attack track; a
      // weapon-less idle default is the correct read-only placeholder.
      autoAttackEnabled: true,
      autoCastState: { kind: "idle" as const },
      activeRecasts: [],
      cooldownReadyAt: cd,
      // #84: client-side SimEntity rebuild carries no per-slot charge state
      // (targeting only reads position/faction); an empty list = all
      // single-cooldown, which is correct for this read-only reconstruction.
      chargeSlots: [],
      gcdReadyAt: asTick(e.gcdReadyAtTick),
      targets: {
        hostile: e.targets.hostile === null ? null : asEntityId(e.targets.hostile),
        friendly: e.targets.friendly === null ? null : asEntityId(e.targets.friendly),
      },
      // #422: reconstruct the aura list from the wire so `isStealthed` /
      // `isTargetableBy` (read transitively by every helper this module
      // delegates to — e.g. `enemyByIndex`'s stealth-skip, #91) see the SAME
      // stealth state the sim does. Only `def` + `expiresAtTick` are ever
      // read by targeting; the rest are structural placeholders (this
      // reconstruction is READ-ONLY — targeting never mutates an aura), same
      // discipline as every other field above.
      auras: e.auras.map(
        (a): AuraInstance => ({
          def: a.def,
          sourceSeat: asSeatIndex(a.sourceSeat),
          appliedAtTick: asTick(a.appliedAtTick),
          expiresAtTick: a.expiresAtTick === null ? null : asTick(a.expiresAtTick),
          stacks: a.stacks,
          absorbRemaining: a.absorbRemaining,
          triggerCounts: [],
          damageAccum: 0,
          forcedPath: null,
          pointsSnapshot: 0,
        }),
      ),
      // #89: targeting reconstruction never reads the resource pool; the wire
      // snapshot omits it, so `null` is the correct read-only placeholder.
      resource: null,
      // #90: targeting reconstruction never reads the point pool; the wire
      // snapshot omits it, so `null` is the correct read-only placeholder.
      points: null,
      // #100: per-school interrupt-lockout vector (fixed width SCHOOL_COUNT=7).
      // Targeting helpers never read it; a fully-unlocked (all-zero) vector is
      // the correct read-only placeholder for this wire reconstruction.
      schoolLockedUntil: [asTick(0), asTick(0), asTick(0), asTick(0), asTick(0), asTick(0), asTick(0)],
      // #565: targeting reconstruction never reads the cursor/aim point; the
      // wire snapshot omits it, so `null` is the correct read-only placeholder.
      aimPoint: null,
    };
    return simEntity;
  });
  return {
    tick: asTick(snapshot.tick),
    phase:
      snapshot.phase === "countdown"
        ? { kind: "countdown", endsAtTick: asTick(0) }
        : snapshot.phase === "active"
          ? { kind: "active" }
          : { kind: "ended", winner: null },
    entities,
    // Client-side target helpers never inspect in-flight projectiles.
    projectiles: [],
    // ...nor persistent ground zones.
    zones: [],
    // ...nor dynamic-terrain walls (#129).
    walls: [],
    // ...nor the match-scoped projectile time-scale modifier (#402).
    projectileTimeScale: null,
  };
}
